import { useQuery } from "@apollo/client";
import { XIcon } from "@heroicons/react/outline";
import { useMemo, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import useDebounce from "@components/use-debounce/use-debounce";

import routineMeetingGroupsQuery from "../../graphql/routine-meeting-groups-query";

export type SelectedMeetingGroup = {
  id: number;
  title: string;
};

type MeetingGroupNode = {
  id: number;
  title: string | null;
  isFormalOneonone: boolean | null;
  recurrenceRule: string | null;
};

type Props = {
  selected: SelectedMeetingGroup[];
  onChange: (selected: SelectedMeetingGroup[]) => void;
};

const RoutineMeetingGroupMultiselect: React.FC<Props> = ({
  selected,
  onChange,
}) => {
  const label = useLabel();
  const organizationId = currentOrganizationVar().id;

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 250);

  const { data, loading } = useQuery(routineMeetingGroupsQuery, {
    variables: { organizationId, search: debouncedQuery || null },
    fetchPolicy: "cache-and-network",
  });

  const selectedIds = useMemo(
    () => new Set(selected.map((group) => group.id)),
    [selected],
  );

  const options: ComboboxGenericOption<number>[] = useMemo(() => {
    const edges = data?.meetingGroups?.edges ?? [];
    return (
      edges
        .map((edge: { node: MeetingGroupNode } | null) => edge?.node)
        .filter(
          (node: MeetingGroupNode | undefined): node is MeetingGroupNode =>
            Boolean(node),
        )
        // Only recurring meeting series (those with a recurrence rule) can be targeted by a routine.
        .filter((node: MeetingGroupNode) => Boolean(node.recurrenceRule))
        // node.id is a GraphQL ID (serialized as a string); coerce to a number so
        // stored meeting_group_ids stay ints and dedup against selectedIds works.
        .filter((node: MeetingGroupNode) => !selectedIds.has(Number(node.id)))
        .map((node: MeetingGroupNode) => {
          const title = node.title || "Untitled meeting";
          return {
            value: Number(node.id),
            label: node.isFormalOneonone
              ? `${label("oneonone", { capitalize: true })}: ${title}`
              : title,
          };
        })
    );
  }, [data, selectedIds, label]);

  const handleAdd = (option: ComboboxGenericOption<number>) => {
    if (!option) return;
    if (selectedIds.has(option.value)) return;
    onChange([...selected, { id: option.value, title: option.label }]);
    setQuery("");
  };

  const handleRemove = (id: number) => {
    onChange(selected.filter((group) => group.id !== id));
  };

  return (
    <div className="flex flex-col gap-2" aria-label="Routine meeting groups">
      <ComboboxGeneric<number>
        value={null}
        options={options}
        onChangeValue={handleAdd}
        query={query}
        setQuery={setQuery}
        loading={loading}
        placeholder="Add a recurring meeting..."
        searchPlaceholder="Search recurring meetings..."
        aria-label="Routine meeting group select"
      />
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((group) => (
            <span
              key={group.id}
              className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs rounded-full pl-3 pr-1.5 py-1"
              aria-label="Routine meeting group pill"
            >
              {group.title}
              <button
                type="button"
                onClick={() => handleRemove(group.id)}
                className="rounded-full p-0.5 hover:bg-gray-300/60 text-gray-400"
                aria-label={`Remove meeting ${group.title}`}
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoutineMeetingGroupMultiselect;
